import React, { useState, useMemo } from 'react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import OperatorLayout from '@/layouts/OperatorLayout'
import PricingRulesNav from '@/components/pricing-rules/PricingRulesNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { currencySymbol as getCurrencySymbol } from '@/lib/calendar-utils'
import { formatMoneySym } from '@/lib/format'
import { ArrowRight, TrendingUp, TrendingDown, Info } from 'lucide-react'
import type { SharedProps } from '@/types/shared'

interface PriceGuardrailsProps extends SharedProps {
  roundingIncrement: number | null
  roundingStrategy: string
  dynamicFloorEnabled: boolean
  dynamicFloorPct: number
  rateStabilityEnabled: boolean
  maxIncreasePctPerCycle: number
  maxDecreasePctPerCycle: number
}

export default function PriceGuardrails({
  currentUser,
  property,
  properties,
  roundingIncrement: initialRoundingIncrement,
  roundingStrategy: initialRoundingStrategy,
  dynamicFloorEnabled: initialDynamicFloorEnabled,
  dynamicFloorPct: initialDynamicFloorPct,
  rateStabilityEnabled: initialRateStabilityEnabled,
  maxIncreasePctPerCycle: initialMaxIncreasePct,
  maxDecreasePctPerCycle: initialMaxDecreasePct,
}: PriceGuardrailsProps) {
  const base = property.basePath
  const sym = getCurrencySymbol(property.currency)

  // Rounding
  const [roundingEnabled, setRoundingEnabled] = useState(
    initialRoundingIncrement != null && initialRoundingIncrement > 0
  )
  const [roundingIncrement, setRoundingIncrement] = useState(
    (initialRoundingIncrement ?? 1).toString()
  )
  const [roundingStrategy, setRoundingStrategy] = useState(initialRoundingStrategy)

  // Dynamic floor
  const [dynamicFloorEnabled, setDynamicFloorEnabled] = useState(initialDynamicFloorEnabled)
  const [dynamicFloorPct, setDynamicFloorPct] = useState(initialDynamicFloorPct.toString())

  // Rate stability
  const [rateStabilityEnabled, setRateStabilityEnabled] = useState(initialRateStabilityEnabled)
  const [maxIncreasePct, setMaxIncreasePct] = useState(initialMaxIncreasePct.toString())
  const [maxDecreasePct, setMaxDecreasePct] = useState(initialMaxDecreasePct.toString())

  const [saving, setSaving] = useState(false)
  const review = usePricingReview()

  const isDirty = useMemo(() => {
    if (roundingEnabled !== (initialRoundingIncrement != null && initialRoundingIncrement > 0))
      return true
    if (roundingIncrement !== (initialRoundingIncrement ?? 1).toString()) return true
    if (roundingStrategy !== initialRoundingStrategy) return true
    if (dynamicFloorEnabled !== initialDynamicFloorEnabled) return true
    if (dynamicFloorPct !== initialDynamicFloorPct.toString()) return true
    if (rateStabilityEnabled !== initialRateStabilityEnabled) return true
    if (maxIncreasePct !== initialMaxIncreasePct.toString()) return true
    if (maxDecreasePct !== initialMaxDecreasePct.toString()) return true
    return false
  }, [
    roundingEnabled,
    roundingIncrement,
    roundingStrategy,
    dynamicFloorEnabled,
    dynamicFloorPct,
    rateStabilityEnabled,
    maxIncreasePct,
    maxDecreasePct,
    initialRoundingIncrement,
    initialRoundingStrategy,
    initialDynamicFloorEnabled,
    initialDynamicFloorPct,
    initialRateStabilityEnabled,
    initialMaxIncreasePct,
    initialMaxDecreasePct,
  ])

  // Reviewed on the server first (change safety R1.5), then sent by the same PATCH.
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    const payload = {
      rounding_increment: roundingEnabled ? Number(roundingIncrement) || 0 : null,
      rounding_strategy: roundingStrategy,
      dynamic_floor_enabled: dynamicFloorEnabled,
      dynamic_floor_pct: Number(dynamicFloorPct) || 80,
      rate_stability_enabled: rateStabilityEnabled,
      max_increase_pct_per_cycle: Number(maxIncreasePct) || 15,
      max_decrease_pct_per_cycle: Number(maxDecreasePct) || 5,
    }
    review.open(`${base}/pricing-rules/price-guardrails/review`, payload, () =>
      inertiaSave('patch', `${base}/pricing-rules/price-guardrails`, payload, {
        onStart: () => setSaving(true),
        onFinish: () => setSaving(false),
      })
    )
  }

  // Rounding preview examples
  const roundingExamples = useMemo(() => {
    const inc = Number(roundingIncrement) || 1
    const samples = [97, 103, 112, 127, 148, 193]
    return samples.map((raw) => {
      let rounded: number
      if (roundingStrategy === 'up') {
        rounded = Math.ceil(raw / inc) * inc
      } else if (roundingStrategy === 'down') {
        rounded = Math.floor(raw / inc) * inc
      } else {
        rounded = Math.round(raw / inc) * inc
      }
      return { raw, rounded }
    })
  }, [roundingIncrement, roundingStrategy])

  // Dynamic floor preview
  const floorPct = Number(dynamicFloorPct) || 80

  // Rate stability worked example — illustrative only, anchored to a round
  // £100 previously-published rate so the percentages map directly to pounds.
  const incPct = Number(maxIncreasePct) || 15
  const decPct = Number(maxDecreasePct) || 5
  const stabilityBase = 100
  const spikeComputed = 165
  const dropComputed = 70
  const cappedUp = stabilityBase + incPct
  const dampenedDown = stabilityBase - decPct

  const STRATEGY_DESCRIPTIONS: Record<string, string> = {
    nearest: 'Rounds to the closest increment. £127 with £5 increment = £125. Most natural-looking prices.',
    up: 'Always rounds up. £123 with £5 increment = £125. Maximises revenue but prices are never below computed rate.',
    down: 'Always rounds down. £127 with £5 increment = £125. Slightly more competitive but you may leave small amounts on the table.',
  }

  return (
    <OperatorLayout title="Price Guardrails" currentUser={currentUser} property={property} properties={properties}>
      <div className="space-y-6">
        <PricingRulesNav
          propertyBasePath={property.basePath}
          activeTab="price-guardrails"
          help={{
            content:
              'Guardrails are the final safety net in the rate engine — they run after all other adjustments. Rounding cleans up odd numbers, the dynamic floor stops rates dropping too low after heavy discounting, and rate stability limits how fast a rate can change between cycles.',
          }}
        />

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Price Rounding Section */}
          <div className="animate-slide-in rounded-xl border border-border/60 bg-white shadow-sm" style={{ animationDelay: '100ms' }}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="flex items-center gap-1">
                    <h3 className="text-sm font-semibold text-foreground">Price Rounding</h3>
                    <HelpTooltip content="Rounds the final computed rate to clean price points. Applied as the second-to-last step in the pipeline, just before restrictions. This makes prices look more professional and intentional." />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Round computed rates to clean price points. Guests perceive rounded prices as more deliberate and trustworthy than odd numbers like {sym}127.43.
                  </p>
                </div>
                <Switch checked={roundingEnabled} onCheckedChange={setRoundingEnabled} />
              </div>
              {roundingEnabled && (
                <div className="mt-4 space-y-4">
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground whitespace-nowrap">
                        Increment
                      </Label>
                      <div className="relative w-24">
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          className="pl-6 text-center font-semibold"
                          value={roundingIncrement}
                          onChange={(e) => setRoundingIncrement(e.target.value)}
                        />
                        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          {sym}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground whitespace-nowrap">
                        Strategy
                      </Label>
                      <Select value={roundingStrategy} onValueChange={setRoundingStrategy}>
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="nearest">Nearest</SelectItem>
                          <SelectItem value="up">Round Up</SelectItem>
                          <SelectItem value="down">Round Down</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Strategy description */}
                  <p className="text-xs text-muted-foreground">
                    {STRATEGY_DESCRIPTIONS[roundingStrategy]}
                  </p>

                  {/* Rounding preview */}
                  <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Preview</span>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {roundingExamples.map(({ raw, rounded }) => (
                        <div key={raw} className="inline-flex items-center gap-1.5 rounded-md bg-white border border-border/40 px-2.5 py-1.5 text-xs">
                          <span className="text-muted-foreground tabular-nums">{sym}{raw}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground/40" />
                          <span className={cn('font-semibold tabular-nums', raw !== rounded ? 'text-primary' : 'text-foreground')}>
                            {sym}{rounded}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Dynamic Floor Section */}
          <div className="animate-slide-in rounded-xl border border-border/60 bg-white shadow-sm" style={{ animationDelay: '150ms' }}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="flex items-center gap-1">
                    <h3 className="text-sm font-semibold text-foreground">Dynamic Floor</h3>
                    <HelpTooltip content="Prevents rates from dropping below a percentage of the 30-day historical average. Even if demand is very low and multiple discounts stack up, the rate will never fall below this threshold. This protects your revenue during off-peak periods." />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Set a minimum rate based on recent performance. Even if demand is very low and discounts stack up, rates won't drop below this percentage of your 30-day average rate. This protects against race-to-the-bottom pricing.
                  </p>
                </div>
                <Switch checked={dynamicFloorEnabled} onCheckedChange={setDynamicFloorEnabled} />
              </div>
              {dynamicFloorEnabled && (
                <div className="mt-4 space-y-4">
                  <div className="flex items-center gap-3">
                    <Label className="text-sm text-muted-foreground">
                      Minimum % of 30-day average
                    </Label>
                    <div className="relative w-24">
                      <Input
                        type="number"
                        step="1"
                        min="50"
                        max="100"
                        className="pr-6 text-center font-semibold"
                        value={dynamicFloorPct}
                        onChange={(e) => setDynamicFloorPct(e.target.value)}
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        %
                      </span>
                    </div>
                  </div>

                  {/* Dynamic floor preview */}
                  <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Example</span>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      If your 30-day average rate is <strong className="text-foreground">{sym}120</strong>,
                      the dynamic floor is <strong className="text-foreground">{formatMoneySym(Math.round(120 * floorPct / 100), sym)}</strong> ({floorPct}%).
                      No rate will drop below {formatMoneySym(Math.round(120 * floorPct / 100), sym)} even if demand adjustments and discounts would push it lower.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Rate Stability Section */}
          <div className="animate-slide-in rounded-xl border border-border/60 bg-white shadow-sm" style={{ animationDelay: '200ms' }}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="flex items-center gap-1">
                    <h3 className="text-sm font-semibold text-foreground">Rate Stability</h3>
                    <HelpTooltip content="Limits how far a rate can move from the previously published rate in one computation cycle. This is a SPEED limit, not a price limit — different from Dynamic Floor, which sets an absolute minimum. Increases and decreases have separate caps. Manual overrides always bypass it." />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Smooths rate movement between cycles so guests and channels never see a jarring overnight swing. <strong className="text-foreground">Dynamic Floor controls how low a rate can go; Rate Stability controls how fast it can change</strong> from where it already is. The two are independent — use either alone or both together.
                  </p>
                </div>
                <Switch checked={rateStabilityEnabled} onCheckedChange={setRateStabilityEnabled} />
              </div>
              {rateStabilityEnabled && (
                <div className="mt-4 space-y-4">
                  <div className="flex flex-wrap items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground whitespace-nowrap">
                        Max increase per cycle
                      </Label>
                      <div className="relative w-24">
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          max="100"
                          className="pr-6 text-center font-semibold"
                          value={maxIncreasePct}
                          onChange={(e) => setMaxIncreasePct(e.target.value)}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          %
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground whitespace-nowrap">
                        Max decrease per cycle
                      </Label>
                      <div className="relative w-24">
                        <Input
                          type="number"
                          step="1"
                          min="1"
                          max="50"
                          className="pr-6 text-center font-semibold"
                          value={maxDecreasePct}
                          onChange={(e) => setMaxDecreasePct(e.target.value)}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                          %
                        </span>
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Increases apply faster than decreases by design: you capture rising demand quickly,
                    but ease rates down gently so a brief dip does not trigger a sell-off.
                  </p>

                  {/* Worked example */}
                  <div className="rounded-lg border border-border/40 bg-muted/20 p-3">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Example</span>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Yesterday you published <strong className="text-foreground tabular-nums">{formatMoneySym(stabilityBase, sym)}</strong>. This cycle the engine recomputes:
                    </p>
                    <div className="mt-2 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />
                        <div className="inline-flex items-center gap-1.5 rounded-md bg-white border border-border/40 px-2.5 py-1.5 text-xs">
                          <span className="text-muted-foreground tabular-nums">{formatMoneySym(spikeComputed, sym)}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground/40" />
                          <span className="font-semibold tabular-nums text-emerald-600">{formatMoneySym(cappedUp, sym)}</span>
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          spike capped to +{incPct}% — climb continues next cycle
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <TrendingDown className="h-3.5 w-3.5 text-rose-600" />
                        <div className="inline-flex items-center gap-1.5 rounded-md bg-white border border-border/40 px-2.5 py-1.5 text-xs">
                          <span className="text-muted-foreground tabular-nums">{formatMoneySym(dropComputed, sym)}</span>
                          <ArrowRight className="h-3 w-3 text-muted-foreground/40" />
                          <span className="font-semibold tabular-nums text-rose-600">{formatMoneySym(dampenedDown, sym)}</span>
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          drop dampened to −{decPct}% — eases down gradually
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Interaction with Dynamic Floor */}
                  {dynamicFloorEnabled && (
                    <div className="flex gap-2.5 rounded-lg border-l-[3px] border-l-primary/50 bg-primary/[0.04] px-3.5 py-2.5">
                      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        <strong className="text-foreground">Both guardrails are on.</strong> They stack —
                        each cycle the rate is held at whichever lower bound is stricter: the Dynamic Floor
                        level (a % of your 30-day average) or last published rate minus {decPct}%. Stability
                        also caps the upside, which Dynamic Floor never does.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={!isDirty || saving} className="rounded-lg px-6">
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
      {review.dialog}
    </OperatorLayout>
  )
}
